import React, { createContext, useContext, useState, useCallback } from 'react';
import { errorHandler } from 'apiHelpers/api/errorHandler';
import { get } from 'apiHelpers/api/ApiKit';
import { errorNotification } from 'utils/Utils';

const initialInfo = {
    brands: [],
    metaData: {
      total_records: 0,
      page: 1,
      limit: 10,
    }
  };
  
const CatalogContext = createContext({
    selectedFilters: [],
    locationInfo: null,
    searchKeyword: '',
    initialFetchedInfo: initialInfo,
    fetchedInfo: { ...initialInfo },
    loading: false,
    loadingMore: false,
    error: null,
    setSelectedFilters: () => {},
    setLocationInfo: () => {},
    setSearchKeyword: () => {},
    setInitialFetchedInfo: () => {},
    setFetchedInfo: () => {},
    setLoading: () => {},
    setLoadingMore: () => {},
    setError: () => {},
    makeApiCall: () => {},
    resetBrands: () => {},
    fetchBrands: () => {},
    loadMoreBrands: () => {},
});

export const CatalogProvider = ({ children }) => {
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [locationInfo, setLocationInfo] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [initialFetchedInfo, setInitialFetchedInfo] = useState(initialInfo);
  const [fetchedInfo, setFetchedInfo] = useState({ ...initialInfo });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const makeApiCall = useCallback(async (requestFn = async () => {}, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      return await requestFn();
    } catch (err) {
      const errorMessage = errorHandler(err?.message || err?.error?.message || "Please try again later");
      setError(err);
      console.error(err);
      errorNotification(errorMessage);
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, []);

  const resetBrands = useCallback(() => {
    setFetchedInfo({ ...initialInfo });
  }, []);

  const fetchBrands = useCallback(({ searchKeyword, selectedFilters, page = 1, limit = 10, reset = false, isAICatalog, locationInfo }) => {
    if (reset) {
      resetBrands();
    }

    makeApiCall(async () => {
      const response = await get('/catalog/get-all-catalog-enabled-brands', {params:{searchKeyword, selectedFilters, page, limit, isAICatalog, locationInfo}});
      setInitialFetchedInfo({
        brands: response?.data?.brands || [],
        metaData: {
          ...response?.data?.metaData,
          page
        }
      });
      setFetchedInfo({
        brands: response?.data?.brands || [],
        metaData: {
          ...response?.data?.metaData,
          page
        }
      });
    });
  }, [resetBrands, makeApiCall]);

  const loadMoreBrands = useCallback(({ isAICatalog }) => {
    const nextPage = fetchedInfo?.metaData?.page + 1;
    const hasMorePages = fetchedInfo?.brands?.length < fetchedInfo?.metaData?.total_records;

    if (!hasMorePages || loadingMore) return;

    makeApiCall(async () => {
      const response = await get('/catalog/get-all-catalog-enabled-brands', {params:{
        ...fetchedInfo?.metaData,
        page: nextPage,
        isAICatalog
      }});

      const updateObj = {
        brands: [...fetchedInfo?.brands, ...response?.data?.brands],
        metaData: {
          ...response?.data?.metaData,
          page: nextPage
        }
      };

      setInitialFetchedInfo(JSON.parse(JSON.stringify(updateObj)));
      setFetchedInfo(JSON.parse(JSON.stringify(updateObj)));
    }, true);
  }, [fetchedInfo, loadingMore, makeApiCall]);

  return (
    <CatalogContext.Provider value={{
      selectedFilters,
      fetchedInfo,
      loading,
      loadingMore,
      error,
      locationInfo,
      searchKeyword,
      initialFetchedInfo,
      setSearchKeyword,
      setSelectedFilters,
      setLocationInfo,
      setFetchedInfo,
      setLoading,
      setLoadingMore,
      setError,
      makeApiCall,
      resetBrands,
      fetchBrands,
      loadMoreBrands,
    }}>
      {children}
    </CatalogContext.Provider>
  );
};

export const useCatalogContext = () => {
  const context = useContext(CatalogContext);
  if (!context) {
    throw new Error('useCatalogContext must be used within a CatalogProvider');
  }
  return context;
};

export default CatalogProvider;